import React from "react";
import APIService from "../../Service/APIService";
import GenericDashboard from "../GenericDashboard/GenericDashboard";
import { Button, Form, Row, Col, Modal, message } from "antd";
import Swal from "sweetalert2";
import Select from "react-select";
import { useAuth } from "../AuthProvider.js";
import { hasPermission } from "../../Config/permissions.js";
import SectionHeader from "../../common/section-header/index.js";

const AccountManufacturer = (props) => {
  const { accountData } = props;
  const readOnly = props.readOnly === true;
  const [manufacturers, setManufacturers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const { user } = useAuth();
  const userData = user;
  const requestVersion = React.useRef(0);
  const accountID = accountData?.ID;
  const currentAccountID = React.useRef(accountID);
  currentAccountID.current = accountID;
  const getManufacturersFromAccount = React.useCallback(async () => {
    const version = ++requestVersion.current;
    const apiServe = new APIService();
    // console.log("Account Data: ", accountData);
    if (accountID) {
      const response = await apiServe.getDataFromBackend(
        `getmanufacturersfromaccount?ID=${accountID}`
      );
      if (version !== requestVersion.current || currentAccountID.current !== accountID) return;
      if (!Array.isArray(response?.data?.data)) throw new Error("Unable to refresh manufacturers.");
      const tempManufacturers = response.data.data.map((item) => ({
          ...item,
          Status: item.IsApproved ? "" : "Pending",
      }));

      setManufacturers(tempManufacturers);
    }
  }, [accountID]);
  const [modalManufacturerVisible, setModalManufacturerVisible] =
    React.useState(false);
  const handleManufacturerNew = () => {
    if (readOnly) return;
    setModalManufacturerVisible(true);
  };

  const handleModalOk = () => {
    setModalManufacturerVisible(false);
  };
  const handleModalCancel = () => {
    setModalManufacturerVisible(false);
  };
  const [manufacturerOptions, setManufacturerOptions] = React.useState([]);
  const [selectedManufacturere, setSelectedManufacturer] = React.useState(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const getAllManufacturersOptions = React.useCallback(async () => {
    const apiServe = new APIService();
    const response = await apiServe.getDataFromBackend("getmanufactureroptions");
    // console.log("All Manufacturers: ", response?.data?.data);
    let tempOptions = [];
    const activeManufacturers = Array.isArray(response?.data?.data)
      ? response.data.data.filter((item) => item?.IsActive !== false)
      : [];
    activeManufacturers.forEach((item) => {
      tempOptions.push({
        value: item.ID,
        label: item.ManufacturerName,
        NeedsApproval: item.NeedsApproval,
      });
    });
    setManufacturerOptions(tempOptions);
  }, []);

  const addManufacturer = async () => {
    if (readOnly) return;
    // console.log("Add Manufacturer");
    if (selectedManufacturere && accountData) {
      Swal.fire({
        title: "Are you sure?",
        text: "Do you want to add this manufacturer to this account?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "No",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            setIsSubmitting(true);
            const apiServe = new APIService();
            const postObj = {
              AccountID: accountData.ID,
              ManufacturerID: selectedManufacturere.value,
            };
            const response = await apiServe.updateDataToBackend(
              postObj,
              "addaccounttomanufacturer"
            );

            const isApproved = response?.data?.IsApproved === true;
            const createdProductsCount =
              Number(response?.data?.CreatedProductsCount) || 0;

            await getManufacturersFromAccount();
            setModalManufacturerVisible(false);
            setSelectedManufacturer(null);

            Swal.fire({
              icon: "success",
              title: isApproved ? "Manufacturer Added" : "Approval Required",
              text: isApproved
                ? `Manufacturer approved and ${createdProductsCount} products added.`
                : "Manufacturer added and sent for approval.",
            });
          } catch (error) {
            const errorMessage =
              error?.response?.data?.error ||
              error?.response?.data?.data ||
              "Failed to add manufacturer. Please try again.";

            Swal.fire({
              icon: "error",
              title: "Unable to Add Manufacturer",
              text: errorMessage,
            });
          } finally {
            setIsSubmitting(false);
          }
        }
      });
    } else {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Please select a manufacturer to add",
      });
    }
  };

  React.useEffect(() => {
    let active = true;
    setLoading(true);
    setManufacturers([]);
    getManufacturersFromAccount()
      .catch(() => { if (active) message.error("Unable to load manufacturers. Reload the account to try again."); })
      .finally(() => { if (active) setLoading(false); });
    getAllManufacturersOptions().catch(() => { if (active) message.error("Unable to load manufacturer options."); });
    return () => { active = false; requestVersion.current += 1; };
  }, [getManufacturersFromAccount, getAllManufacturersOptions]);
  return (
    <div>
      {/* {accountData && */}
      <div>
        <SectionHeader
          title="Manufacturers"
          buttonLabel="Add Manufacturer"
          onButtonClick={handleManufacturerNew}
          showButton={userData?.Type === "Admin" && hasPermission(userData, "manage_accounts.edit") && !readOnly}
        />
        <GenericDashboard
          data={manufacturers}
          screen="AccountManufacturer"
          additionalData={accountData}
          readOnly={readOnly}
          loading={loading}
          onManufacturersRefresh={getManufacturersFromAccount}
        />
        {!readOnly && <Modal
          open={modalManufacturerVisible}
          onOk={handleModalOk}
          onCancel={handleModalCancel}
          centered={true}
          footer={null}
          style={{ width: "800px" }}
        >
          <div style={{ padding: "10px 20px 20px 20px" }}>
            <h3>Add Manufacturer to this Account</h3>
            <Form layout="vertical">
              <Row gutter={16}>
                <Col span={18}>
                  <Form.Item label="Manufacturer">
                    <Select
                      defaultValue="Choose Manufacturer"
                      value={selectedManufacturere}
                      onChange={(value) => setSelectedManufacturer(value)}
                      options={manufacturerOptions}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Button
                type="primary"
                style={{ background: "#27a907" }}
                onClick={addManufacturer}
                loading={isSubmitting}
              >
                Add Manufacturer
              </Button>
            </Form>
          </div>
        </Modal>}
      </div>
      {/* } */}
    </div>
  );
};

export default AccountManufacturer;
